import kayrosLogo from "@/assets/kayros-logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative py-16 bg-dark-section-bg border-t border-dark-section-border overflow-hidden">
      {/* Tech Background Elements */}
      
      {/* Fine mesh grid */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--primary) / 0.5) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary) / 0.5) 1px, transparent 1px)
          `,
          backgroundSize: '16px 16px'
        }}
      />

      {/* Larger blueprint grid */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px'
        }}
      />

      {/* Vertical tech lines */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.03]" preserveAspectRatio="none">
        <line x1="10%" y1="0" x2="10%" y2="100%" stroke="hsl(var(--primary))" strokeWidth="1" strokeDasharray="4 8" />
        <line x1="90%" y1="0" x2="90%" y2="100%" stroke="hsl(var(--primary))" strokeWidth="1" strokeDasharray="4 8" />
      </svg>

      {/* Corner accent - top left */}
      <div className="absolute top-0 left-0 w-24 h-24 opacity-[0.06]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M0 0 L0 35 L8 35 L8 8 L35 8 L35 0 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="8" cy="8" r="2" fill="hsl(var(--primary))" />
        </svg>
      </div>

      {/* Corner accent - top right */}
      <div className="absolute top-0 right-0 w-24 h-24 opacity-[0.06]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M100 0 L100 35 L92 35 L92 8 L65 8 L65 0 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="92" cy="8" r="2" fill="hsl(var(--primary))" />
        </svg>
      </div>

      {/* Corner accent - bottom left */}
      <div className="absolute bottom-0 left-0 w-24 h-24 opacity-[0.06]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M0 100 L0 65 L8 65 L8 92 L35 92 L35 100 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="8" cy="92" r="2" fill="hsl(var(--primary))" />
        </svg>
      </div>

      {/* Corner accent - bottom right */}
      <div className="absolute bottom-0 right-0 w-24 h-24 opacity-[0.06]">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <path d="M100 100 L100 65 L92 65 L92 92 L65 92 L65 100 Z" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
          <circle cx="92" cy="92" r="2" fill="hsl(var(--primary))" />
        </svg>
      </div>

      {/* Circuit trace lines */}
      <svg className="absolute left-[5%] top-1/2 -translate-y-1/2 w-20 h-16 opacity-[0.05]">
        <path d="M0 8 L15 8 L20 13 L40 13" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
        <circle cx="40" cy="13" r="2" fill="hsl(var(--primary))" />
      </svg>
      
      <svg className="absolute right-[5%] top-1/2 -translate-y-1/2 w-20 h-16 opacity-[0.05]">
        <path d="M80 8 L65 8 L60 13 L40 13" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" />
        <circle cx="40" cy="13" r="2" fill="hsl(var(--primary))" />
      </svg>

      {/* Data nodes */}
      <div className="absolute top-6 left-[25%] w-1.5 h-1.5 rounded-full bg-primary/15" />
      <div className="absolute top-10 right-[30%] w-1 h-1 rounded-full bg-primary/10" />
      <div className="absolute bottom-8 left-[45%] w-1 h-1 rounded-full bg-primary/10" />
      <div className="absolute top-1/2 right-[12%] w-1 h-1 rounded-full bg-primary/15" />

      {/* Content */}
      <div className="container px-6 relative z-10 pb-[env(safe-area-inset-bottom)]">
        <div className="flex flex-col items-center gap-8">
          {/* Logo */}
          <a href="#" className="flex items-center">
            <img 
              src={kayrosLogo} 
              alt="Kayros" 
              className="h-10 w-auto"
            />
          </a>

          {/* Links */}
          <nav className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3">
            <a
              href="#services"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Services
            </a>
            <a
              href="#why-us"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Why Us
            </a>
            <a
              href="#process"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Process
            </a>
            <a
              href="#team"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Team
            </a>
            <a
              href="#contact"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Contact
            </a>
            <a
              href="/terms-of-use"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Terms of Use
            </a>
            <a
              href="/privacy-policy"
              className="text-sm text-dark-section-foreground/70 hover:text-primary transition-colors duration-200 py-2"
            >
              Privacy Policy
            </a>
          </nav>

          {/* Copyright */}
          <div className="text-center space-y-1">
            <p className="text-sm text-dark-section-foreground/50">
              © 2021-{currentYear} Spets Tech Solution Ltd. All rights reserved.
            </p>
            <p className="text-xs text-dark-section-foreground/40">
              Kayros is a trading name of Spets Tech Solution Ltd.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
